import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchProducts, fetchMenus, fetchDiscounts } from "../../util/customer/httpCustomer";
import ItemCard from "./ItemCard";
import ShopInfo from "./ShopInfo";
import Cart from "./Cart";
import InfoModal from "../shared/InfoModal";
import LoadingOverlay from "../shared/LoadingOverlay";
import "./Shop.css";
import holdingHandDinner from "../../assets/shared/collection_icons/holding-hand-dinner.svg";
import shoppingCart from "../../assets/shared/shopping-bag.svg";
import chevronLeft from "../../assets/admin/icons/chevron-left.svg";
import chevronRight from "../../assets/admin/icons/chevron-right.svg";

const Shop = () => {
   const [isLoading, setIsLoading] = useState(true);
   const [cartItems, setCartItems] = useState(() => {
      const shopId = window.location.pathname.split('/')[1];
      const savedCart = localStorage.getItem(`cartItems_${shopId}`);
      return savedCart ? JSON.parse(savedCart) : [];
   });
   const [selectedProduct, setSelectedProduct] = useState(null);
   const [isModalOpen, setIsModalOpen] = useState(false);
   const cartModalRef = useRef(null);
   const tabsRef = useRef(null);
   const [isCartOpen, setIsCartOpen] = useState(false);
   const [isUpdated, setIsUpdated] = useState(false);
   const updateTimeoutRef = useRef(null);
   const previousShopIdRef = useRef(null);
   const [showLeftArrow, setShowLeftArrow] = useState(false);
   const [showRightArrow, setShowRightArrow] = useState(true);
   const collectionsRefs = useRef({});
   const [activeTab, setActiveTab] = useState(null);
   const tabButtonsRef = useRef({});

   // Clear cart when shop changes
   useEffect(() => {
      const currentShopId = window.location.pathname.split('/')[1];
      if (previousShopIdRef.current && previousShopIdRef.current !== currentShopId) {
         setCartItems([]);
      }
      previousShopIdRef.current = currentShopId;
   }, [window.location.pathname]);

   useEffect(() => {
      const shopId = window.location.pathname.split('/')[1];
      localStorage.setItem(`cartItems_${shopId}`, JSON.stringify(cartItems));
   }, [cartItems]);

   // Set up scroll listener to update active tab based on scroll position
   useEffect(() => {
      const handleScroll = () => {
         // Only run if all collection refs are set
         if (Object.keys(collectionsRefs.current).length === 0) return;

         // Get current scroll position
         const scrollPosition = window.scrollY + 120; // 120px offset to account for sticky header

         // Find the section that is currently visible
         let currentActiveTab = null;
         
         // Convert to array and sort by position
         const sortedSections = Object.entries(collectionsRefs.current)
            .filter(([key, el]) => el) // Filter out nulls
            .sort(([keyA, elA], [keyB, elB]) => {
               const rectA = elA.getBoundingClientRect();
               const rectB = elB.getBoundingClientRect();
               return (rectA.top + window.scrollY) - (rectB.top + window.scrollY);
            });

         // Find the last section that starts before the current scroll position
         for (let i = sortedSections.length - 1; i >= 0; i--) {
            const [key, element] = sortedSections[i];
            const rect = element.getBoundingClientRect();
            const sectionTop = rect.top + window.scrollY;
            
            if (sectionTop <= scrollPosition) {
               currentActiveTab = key;
               break;
            }
         }

         // If no section found, use the first one
         if (!currentActiveTab && sortedSections.length > 0) {
            currentActiveTab = sortedSections[0][0];
         }

         // Update the active tab if it changed
         if (currentActiveTab !== activeTab) {
            setActiveTab(currentActiveTab);
            
            // Update button class
            Object.entries(tabButtonsRef.current).forEach(([key, button]) => {
               if (key === currentActiveTab) {
                  button.classList.add('active');
               } else {
                  button.classList.remove('active');
               }
            });
            
            // Scroll the active tab into view in the tab bar if needed
            const activeButton = tabButtonsRef.current[currentActiveTab];
            if (activeButton && tabsRef.current) {
               const tabsRect = tabsRef.current.getBoundingClientRect();
               const buttonRect = activeButton.getBoundingClientRect();
               
               // If button is not fully visible in the tabs container
               if (buttonRect.left < tabsRect.left || buttonRect.right > tabsRect.right) {
                  activeButton.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
               }
            }
         }
      };

      window.addEventListener('scroll', handleScroll);
      // Run once to set initial state
      setTimeout(handleScroll, 500);

      return () => window.removeEventListener('scroll', handleScroll);
   }, [activeTab]);

   // Check if scroll buttons should be shown
   useEffect(() => {
      const checkScrollButtons = () => {
         if (tabsRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = tabsRef.current;
            setShowLeftArrow(scrollLeft > 0);
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
         }
      };

      // Run checkScrollButtons on mount
      checkScrollButtons();
      
      // Add scroll event listener
      const tabsElement = tabsRef.current;
      if (tabsElement) {
         tabsElement.addEventListener('scroll', checkScrollButtons);
         
         // Also add a resize listener for responsive layouts
         window.addEventListener('resize', checkScrollButtons);
      }
      
      return () => {
         if (tabsElement) {
            tabsElement.removeEventListener('scroll', checkScrollButtons);
            window.removeEventListener('resize', checkScrollButtons);
         }
      };
   }, []); // Empty dependency array, we'll handle updates manually

   const handleScroll = (direction) => {
      if (tabsRef.current) {
         const scrollAmount = 200; // Adjust as needed
         const currentScroll = tabsRef.current.scrollLeft;
         
         if (direction === 'left') {
            tabsRef.current.scrollLeft = currentScroll - scrollAmount;
         } else {
            tabsRef.current.scrollLeft = currentScroll + scrollAmount;
         }
         
         // Force check scroll buttons after scroll
         setTimeout(() => {
            const { scrollLeft, scrollWidth, clientWidth } = tabsRef.current;
            setShowLeftArrow(scrollLeft > 0);
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
         }, 100);
      }
   };

   // Scroll to collection when tab is clicked
   const scrollToCollection = (collectionKey) => {
      const element = collectionsRefs.current[collectionKey];
      if (element) {
         // Scroll into view with smooth behavior
         window.scrollTo({
            top: element.offsetTop - 90, // Offset for the sticky header
            behavior: 'smooth'
         });
      }
   };

   // Toggle mobile cart modal
   const toggleMobileCart = () => {
      setIsCartOpen(!isCartOpen);
      // Prevent body scrolling when modal is open
      document.body.style.overflow = !isCartOpen ? 'hidden' : '';
   };

   // Close mobile cart modal
   const closeMobileCart = () => {
      setIsCartOpen(false);
      document.body.style.overflow = '';
   };

   // Get total items in cart
   const getTotalItems = () => {
      return cartItems.reduce((total, item) => total + item.quantity, 0);
   };

   // Fetch data
   const { data: productsData = [], isLoading: isLoadingProducts } = useQuery({ 
      queryKey: ["products"], 
      queryFn: fetchProducts 
   });
   const { data: menusData = [], isLoading: isLoadingMenus } = useQuery({ 
      queryKey: ["menus"], 
      queryFn: fetchMenus 
   });
   const { data: discountsData = [], isLoading: isLoadingDiscounts } = useQuery({ 
      queryKey: ["discounts"], 
      queryFn: fetchDiscounts 
   });

   // Check if all images are loaded
   useEffect(() => {
      if (!isLoadingProducts && !isLoadingMenus && !isLoadingDiscounts) {
         const preloadImages = async () => {
            const images = document.querySelectorAll('img');
            const imagePromises = Array.from(images).map(img => {
               if (img.complete) return Promise.resolve();
               
               return new Promise((resolve, reject) => {
                  img.onload = resolve;
                  img.onerror = reject;
               });
            });

            try {
               await Promise.all(imagePromises);
               setIsLoading(false);
            } catch (error) {
               console.error('Error loading images:', error);
               // Even if some images fail to load, we still want to show the content
               setIsLoading(false);
            }
         };

         preloadImages();
      }
   }, [isLoadingProducts, isLoadingMenus, isLoadingDiscounts]);

   if (isLoading) {
      return <LoadingOverlay />;
   }

   // Producten en menu's groeperen op 'collection'
   const products = productsData.filter((p) => p.soldAlone);
   const menus = menusData;

   const applyDiscounts = (items, discountType) => {
      return items.map((item) => {
         let applicableDiscounts = [];

         // Check voor specifieke Product/Menu kortingen
         discountsData.productMenuDiscounts.forEach((discount) => {
            if (
               (discount.products && discount.products.some(p => p._id === item._id)) ||
               (discount.menus && discount.menus.some(m => m._id === item._id))
            ) {
               applicableDiscounts.push(discount.discount);
            }
         });

         // Check voor collectie-kortingen
         discountsData.collectionDiscounts.forEach((discount) => {
            if (discount.collection === item.collection) {
               applicableDiscounts.push(discount.discount);
            }
         });

         // Pak de hoogste korting
         const maxDiscount = applicableDiscounts.length > 0 ? Math.max(...applicableDiscounts) : 0;

         return {
            ...item,
            discount: maxDiscount,
         };
      });
   };

   const discountedProducts = applyDiscounts(products, "product");
   const discountedMenus = applyDiscounts(menus, "menu");

   let mergedCollections = {}
   const addToCollection = (items, type) => {
      items.forEach(({ collection, ...item }) => {
         const key = collection?.name || "overige";
         if (!mergedCollections[key]) {
            mergedCollections[key] = {
               items: [],
               icon: collection?.icon || "",
               sortOrder: collection?.sortOrder ?? 9999 // Default sort order for "overige"
            };
         }
         mergedCollections[key].items.push({ ...item, type });
      });
   };

   addToCollection(discountedProducts, "product");
   addToCollection(discountedMenus, "menu");

   // Sort collections by sortOrder
   const sortedCollections = Object.entries(mergedCollections)
         .sort(([aKey, aValue], [bKey, bValue]) => {
            const orderA = aValue.sortOrder;
            const orderB = bValue.sortOrder;
            return orderA - orderB;
      });

   // Make sure "overige" is always last
   const collectionsWithoutOverige = sortedCollections.filter(([key]) => key !== "overige");
   const overigeCollection = sortedCollections.find(([key]) => key === "overige");
   
   // Final ordered collections array
   const orderedCollections = [...collectionsWithoutOverige];
   if (overigeCollection) {
      orderedCollections.push(overigeCollection);
   }

   // Tabs for navigation
   const tabs = orderedCollections.map(([key]) => key);

   const handleAddToCart = (item) => {
      setCartItems((prev) => {
         // Check if there's an existing item with the same ID and options
         const existingItemIndex = prev.findIndex(
            (cartItem) => {
               // Check if it's the same product
               if (cartItem._id !== item._id) return false;

               // If both items have no options, they're the same
               if (!cartItem.selectedOptions && !item.selectedOptions) return true;
               if (!cartItem.selectedOptions || !item.selectedOptions) return false;

               // Compare options
               const cartItemOptions = cartItem.selectedOptions;
               const newItemOptions = item.selectedOptions;

               // Check if all options match
               return Object.keys(cartItemOptions).every(key => {
                  const cartValue = cartItemOptions[key];
                  const newValue = newItemOptions[key];

                  // For arrays (multiselect), compare sorted arrays
                  if (Array.isArray(cartValue) && Array.isArray(newValue)) {
                     return JSON.stringify(cartValue.sort()) === JSON.stringify(newValue.sort());
                  }

                  // For other types, direct comparison
                  return cartValue === newValue;
               });
            }
         );

         if (existingItemIndex !== -1) {
            // Update quantity of existing item
            return prev.map((cartItem, index) =>
               index === existingItemIndex
                  ? { ...cartItem, quantity: cartItem.quantity + 1 }
                  : cartItem
            );
         }

         // Add new item with quantity 1
         return [...prev, { ...item, quantity: 1 }];
      });

      // Clear previous timeout
      if (updateTimeoutRef.current) {
         clearTimeout(updateTimeoutRef.current);
      }

      // Remove 'updated' class, wait for 20ms, and then re-add it
      setIsUpdated(false);

      updateTimeoutRef.current = setTimeout(() => {
         setIsUpdated(true);
         updateTimeoutRef.current = setTimeout(() => setIsUpdated(false), 3000);
      }, 20);
   };

   const handleIncreaseQuantity = (id, selectedOptions) => {
      setCartItems((prev) =>
         prev.map((item) =>
            item._id === id && JSON.stringify(item.selectedOptions) === JSON.stringify(selectedOptions)
               ? { ...item, quantity: item.quantity + 1 }
               : item
         )
      );
   };

   const handleDecreaseQuantity = (id, selectedOptions) => {
      setCartItems((prev) =>
         prev
            .map((item) =>
               item._id === id && JSON.stringify(item.selectedOptions) === JSON.stringify(selectedOptions)
                  ? { ...item, quantity: item.quantity - 1 }
                  : item
            )
            .filter((item) => item.quantity > 0)
      );
   };

   const handleItemClick = (item) => {
      setSelectedProduct(item);
      setIsModalOpen(true);
   };

   const handleUpdateCartItems = (updatedCartItems) => {
      const shopId = window.location.pathname.split('/')[1];
      localStorage.setItem(`cartItems_${shopId}`, JSON.stringify(updatedCartItems));
      setCartItems(updatedCartItems);
   };

   return (
      <div className="shop-container fade-in">
         <ShopInfo></ShopInfo>

         {/* Filter-opties */}
         <div className="shop-content g-20 br-15 g-5">
            {/* Tabs navigation */}
            <div className="collection-wrapper sticky-tabs">
                  {showLeftArrow && (
                     <button className="scroll-arrow left" onClick={() => handleScroll('left')}>
                        <img src={chevronLeft} alt="Scroll left" />
                     </button>
                  )}
                  
                  <div className="tabs-customer br-10" ref={tabsRef}>
                     {tabs.map((tab) => (
                        <button
                           key={tab}
                        className={`tab-button-customer f-m ${activeTab === tab ? 'active' : ''}`}
                        onClick={() => scrollToCollection(tab)}
                        ref={el => tabButtonsRef.current[tab] = el}
                        >
                           {tab === "overige" ? (
                              <img src={holdingHandDinner} alt="Overige" className="collection-icon" />
                           ) : (
                              <img 
                                 src={mergedCollections[tab].icon} 
                                 alt={tab}
                                 className="collection-icon"
                              />
                           )}
                           {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </button>
                     ))}
                  </div>
                  
                  {showRightArrow && (
                     <button className="scroll-arrow right" onClick={() => handleScroll('right')}>
                        <img src={chevronRight} alt="Scroll right" />
                     </button>
                  )}
               </div>

            {/* Display all collections */}
            <div className="collections-container">
               {orderedCollections.map(([collectionKey, collection]) => (
                  <div 
                     key={collectionKey} 
                     className="collection-section"
                     ref={el => collectionsRefs.current[collectionKey] = el}
                     id={`collection-${collectionKey}`}
                  >
                     <div className="collection-header">
                        {collectionKey === "overige" ? (
                           <img src={holdingHandDinner} alt="Overige" className="collection-icon-large" />
                        ) : (
                           <img 
                              src={collection.icon} 
                              alt={collectionKey}
                              className="collection-icon-large"
                           />
                        )}
                        <h2 className="collection-title">{collectionKey.charAt(0).toUpperCase() + collectionKey.slice(1)}</h2>
                     </div>

                     {collection.items && collection.items.length > 0 ? (
                     <div className="product-grid a-i-s">
                           {collection.items.map((item) => (
                           <ItemCard
                              key={item._id}
                              product={item}
                              productOptions={item.productOptions}
                              onItemClick={() => handleItemClick(item)}
                           />
                        ))}
                     </div>
                  ) : (
                        <p>Geen items in deze collectie.</p>
                  )}
               </div>
               ))}
            </div>
         </div>

         {/* Mobile Cart Button */}
         <button 
            className="mobile-cart-button" 
            onClick={toggleMobileCart}
            aria-label="Open winkelmandje"
         >
            <img src={shoppingCart} alt="Winkelmandje" />
            {getTotalItems() > 0 && (
               <span className="cart-badge">{getTotalItems()}</span>
            )}
         </button>

         {/* Desktop Cart (Always visible on desktop) */}
         <div className="desktop-cart">
            <Cart
               cartItems={cartItems}
               onIncreaseQuantity={handleIncreaseQuantity}
               onDecreaseQuantity={handleDecreaseQuantity}
               onUpdateCartItems={handleUpdateCartItems}
               id="desktop-cart"
            />
         </div>

         {/* Mobile Cart Modal (Only visible when toggled on mobile) */}
         {isCartOpen && (
            <div className="cart-modal-overlay" onClick={closeMobileCart}>
               <div className="cart-modal" onClick={(e) => e.stopPropagation()}>
         <Cart
            cartItems={cartItems}
            onIncreaseQuantity={handleIncreaseQuantity}
            onDecreaseQuantity={handleDecreaseQuantity}
            onUpdateCartItems={handleUpdateCartItems}
                     id="mobile-cart"
                     onClose={closeMobileCart}
         />
               </div>
            </div>
         )}

         {selectedProduct && (
            <InfoModal
               isOpen={isModalOpen}
               onClose={() => setIsModalOpen(false)}
               product={selectedProduct}
               onAddToCart={handleAddToCart}
            />
         )}
      </div>
   );
};

export default Shop;
